/**
 * Coordination point between the forms draft-sync loop and the global network
 * error interceptor (OFFICEHUB-CLIENT-57).
 *
 * useFormDraftSync's stated FAILURE POLICY is that a transient failure of a
 * background tick — offline, timeout, a dropped mobile connection — is
 * EXPECTED in the field: it retries on the next tick and says nothing. The
 * network interceptor (lib/datahub/errorLogging/consoleAndNetworkCapture)
 * cannot see caller intent, so it reported exactly the failures the hook is
 * designed to absorb: the observed event is a field device whose Firestore
 * streams had been dropping for minutes, with SUCCESSFUL sync ticks seconds
 * before and after the one fetch that rejected. That violates the hook's
 * policy one layer down, and manufactures a Sentry issue that regresses on
 * every flaky connection forever.
 *
 * The suppression is deliberately scoped three ways, so the real failure
 * classes this URL can express stay visible:
 *
 *   1. Only while a tick's callable round trip is IN FLIGHT. A user-initiated
 *      Save Draft / Submit runs through the React Query mutation in
 *      useFormSubmissions while no tick is in flight (ticks are single-flight
 *      and stop on submit), so a persistent failure — a lost IAM invoker
 *      binding, a CORS misconfiguration — still surfaces through every user
 *      action.
 *   2. Only status 0 (a fetch that never got an HTTP response). HTTP 5xx is
 *      a server-side defect and is never suppressed.
 *   3. Only the draft-sync callable's URL.
 *
 * The residual that this once left open (OFFICEHUB-CLIENT-63, forms_createSubmission, 2026-10-26: a
 * phone tab suspended by the OS, the fetch rejected 211 s later): the SDK's 70s callable timeout
 * fires first, the tick ends (deadline-exceeded, retried silently) while the underlying fetch is
 * still pending, and when that fetch finally rejects no tick is "in flight" any more, so the
 * interceptor reported a failure the hook had already absorbed. The tick now registers that
 * orphaned fetch (markDraftSyncTickOrphaned) and exactly ONE later status-0 report is absorbed per
 * registration, within ORPHAN_TTL_MS, and never while a manual save is in flight.
 */

let inFlightTicks = 0;
let inFlightManualSaves = 0;
/** Timestamps (ms) of ticks the SDK gave up on while their fetch may still be pending. */
let orphanedTickAt: number[] = [];

/** Longest a given-up fetch is expected to stay pending (a suspended phone tab was seen at 211 s). */
const ORPHAN_TTL_MS = 10 * 60 * 1000;
/** A flaky field device cannot leave more than a handful of fetches pending at once. */
const MAX_ORPHANS = 5;

/**
 * A tick's callable was given up by the SDK's own timeout (deadline-exceeded) while the underlying fetch
 * may still be pending. The tick is over and retried next time; when that fetch finally rejects with no
 * response, absorb that one report too. Call only from the tick's own catch, never for a manual save.
 */
export function markDraftSyncTickOrphaned(now: number = Date.now()): void {
  orphanedTickAt = [...orphanedTickAt.filter((t) => now - t < ORPHAN_TTL_MS), now].slice(-MAX_ORPHANS);
}

/** Mark a draft-sync tick's callable round trip as started. */
export function beginDraftSyncTick(): void {
  inFlightTicks += 1;
}

/** Mark it finished (success or failure) — call from a finally block. */
export function endDraftSyncTick(): void {
  inFlightTicks = Math.max(0, inFlightTicks - 1);
}

/**
 * Mark a USER-INITIATED Save Draft / Submit round trip to the same callable
 * (useFormSubmissions.createFormSubmission). The draft-sync loop keeps
 * ticking until the submit RESOLVES (FormFiller calls stop() only after
 * mutateAsync returns), so a tick can genuinely be in flight at the moment a
 * user's own request fails — and the interceptor cannot tell two concurrent
 * requests to one URL apart. While any manual save is in flight, suppression
 * is disabled entirely: the conservative direction is to report, because the
 * user-initiated failure is the canary this policy promises never to hide.
 */
export function beginManualFormSave(): void {
  inFlightManualSaves += 1;
}

/** Mark the manual save finished — call from a finally block. */
export function endManualFormSave(): void {
  inFlightManualSaves = Math.max(0, inFlightManualSaves - 1);
}

/** Test-only: restore pristine state so test files cannot couple via order. */
export function __resetDraftSyncNetworkPolicyForTests(): void {
  inFlightTicks = 0;
  inFlightManualSaves = 0;
  orphanedTickAt = [];
}

/** The callable the draft-sync loop addresses (see useFormDraftSync.runTick). */
const DRAFT_SYNC_CALLABLE = /forms_createSubmission/i;

/**
 * True when a failed request is a draft-sync tick's own transient network
 * failure — the draft-sync callable failing at the network layer (status 0)
 * while a tick is in flight AND no user-initiated save of the same callable
 * is concurrently in flight. The hook owns retry and user notice for this
 * case; reporting it as an error is noise by design.
 */
export function isDraftSyncTransientNetworkFailure(url: string, status: number, now: number = Date.now()): boolean {
  if (status !== 0 || inFlightManualSaves > 0 || !DRAFT_SYNC_CALLABLE.test(url)) return false;
  if (inFlightTicks > 0) return true;
  // No tick in flight: absorb only a late rejection of a fetch an earlier tick gave up on. One registration
  // absorbs one report (consumed here), and an expired one absorbs nothing.
  const live = orphanedTickAt.filter((t) => now - t < ORPHAN_TTL_MS);
  orphanedTickAt = live.slice(1);
  return live.length > 0;
}
